import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { supabase } from '@/integrations/supabase/client';
import * as offline from '@/shared/db/offlineRepository';
import { ContractTemplate, SignedContract, AppSettings } from '@/shared/types';

// ============================================
// BACKGROUND SYNC — géré par SyncProvider
// ============================================

// Conservé pour compatibilité ascendante — SyncProvider gère maintenant le reconnect sync
export function useSyncOnReconnect() {}

// ============================================
// PROFILE / SETTINGS
// ============================================

export function useProfile() {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();

  return useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      // Always read from Dexie first
      const local = await offline.getProfileOffline(user!.id);
      if (local) return local;

      // If nothing in Dexie and online, sync will populate it
      if (isOnline) {
        const { pullProfiles } = await import('@/shared/db/syncEngine');
        await pullProfiles(user!.id);
        return offline.getProfileOffline(user!.id);
      }
      return null;
    },
    enabled: !!user,
  });
}

export function useSettings(): AppSettings {
  const { data: profile } = useProfile();
  return offline.profileToSettings(profile);
}

export function useUpdateProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (updates: Parameters<typeof offline.updateProfileOffline>[1]) => {
      await offline.updateProfileOffline(user!.id, updates);
      // Push immediately if online
      if (isOnline) {
        const { pushPendingProfile } = await import('@/shared/db/syncEngine');
        await pushPendingProfile(user!.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
    },
  });
}

export interface ProfileBilling {
  siret:            string;
  vat_number:       string;
  billing_address:  Record<string, string>;
  invoice_color:    string;
  invoice_prefix:   string;
  quote_prefix:     string;
  payment_terms:    string;
  invoice_footer:   string;
}

export function useProfileBilling() {
  const { user } = useAuth();
  return useQuery<ProfileBilling>({
    queryKey: ['profile-billing', user?.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('profiles')
        .select('siret, vat_number, billing_address, invoice_color, invoice_prefix, quote_prefix, payment_terms, invoice_footer')
        .eq('user_id', user!.id)
        .single();
      return {
        siret:           data?.siret            ?? '',
        vat_number:      data?.vat_number       ?? '',
        billing_address: data?.billing_address  ?? {},
        invoice_color:   data?.invoice_color    ?? '#2eb8aa',
        invoice_prefix:  data?.invoice_prefix   ?? 'FAC',
        quote_prefix:    data?.quote_prefix     ?? 'DEV',
        payment_terms:   data?.payment_terms    ?? '',
        invoice_footer:  data?.invoice_footer   ?? '',
      };
    },
    enabled: !!user,
  });
}

export function useUpdateBillingProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Partial<ProfileBilling>) => {
      const { error } = await (supabase as any)
        .from('profiles')
        .update(updates)
        .eq('user_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['profile-billing', user?.id] });
    },
  });
}

// ============================================
// TEMPLATES
// ============================================

export function useTemplates() {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();

  return useQuery({
    queryKey: ['templates', user?.id],
    queryFn: async () => {
      // Read from Dexie (seeds defaults if empty)
      const templates = await offline.getTemplatesOffline(user!.id);

      // Background sync if online
      if (isOnline) {
        const { pushPendingTemplates, pullTemplates } = await import('@/shared/db/syncEngine');
        pushPendingTemplates(user!.id)
          .then(() => pullTemplates(user!.id))
          .catch(console.error);
      }

      return templates;
    },
    enabled: !!user,
  });
}

export function useSaveTemplate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (template: ContractTemplate) => {
      await offline.saveTemplateOffline(user!.id, template);
      if (isOnline) {
        const { pushPendingTemplates } = await import('@/shared/db/syncEngine');
        await pushPendingTemplates(user!.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', user?.id] });
    },
  });
}

export function useDeleteTemplate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (id: string) => {
      await offline.deleteTemplateOffline(user!.id, id);
      if (isOnline) {
        const { pushPendingTemplates } = await import('@/shared/db/syncEngine');
        await pushPendingTemplates(user!.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', user?.id] });
    },
  });
}

// ============================================
// SIGNED CONTRACTS
// ============================================

export function useSignedContracts() {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();

  return useQuery({
    queryKey: ['signed_contracts', user?.id],
    queryFn: async () => {
      const contracts = await offline.getSignedContractsOffline(user!.id);

      if (isOnline) {
        const { pushPendingSignedContracts, pullSignedContracts } = await import('@/shared/db/syncEngine');
        pushPendingSignedContracts(user!.id)
          .then(() => pullSignedContracts(user!.id))
          .catch(console.error);
      }

      return contracts;
    },
    enabled: !!user,
  });
}

export function useSaveSignedContract() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (contract: SignedContract) => {
      await offline.saveSignedContractOffline(user!.id, contract);
      if (isOnline) {
        const { pushPendingSignedContracts } = await import('@/shared/db/syncEngine');
        await pushPendingSignedContracts(user!.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signed_contracts', user?.id] });
    },
  });
}

export function useDeleteSignedContract() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (id: string) => {
      await offline.deleteSignedContractOffline(user!.id, id);
      if (isOnline) {
        const { pushPendingSignedContracts } = await import('@/shared/db/syncEngine');
        await pushPendingSignedContracts(user!.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signed_contracts', user?.id] });
    },
  });
}
